/* =============================================================
   cyber/verso  ·  client JS
   -------------------------------------------------------------
   File: assets/js/cyberverso.js

   Caricato in coda al body di default.hbs.

   Si occupa di:
     1. Inizializzare il dark mode (default) o light mode (se
        l'utente l'ha scelto in passato), leggendo localStorage.
     2. Esporre una funzione globale cvToggleTheme() che inverte
        la modalita' e salva la scelta.
     3. Aggiornare l'icona del toggle se presente nel DOM.
     4. Lingua corrente su <html data-cv-lang> (path o ?lang=) e link
        /tag/…/ localizzati in italiano sulle pagine IT.
   ============================================================= */

(function () {
  'use strict';

  var STORAGE_KEY = 'cv-theme';
  var THEME_DARK = 'dark';
  var THEME_LIGHT = 'light';

  /**
   * Applica un tema modificando le classi su :root.
   * Source usa .has-light-text per attivare la palette scura.
   * Noi aggiungiamo .cv-light-mode quando l'utente sceglie light.
   */
  function applyTheme(theme) {
    var root = document.documentElement;
    if (theme === THEME_LIGHT) {
      root.classList.remove('has-light-text');
      root.classList.add('cv-light-mode');
    } else {
      root.classList.add('has-light-text');
      root.classList.remove('cv-light-mode');
    }
    updateToggleIcons(theme);
  }

  /**
   * Aggiorna gli elementi con [data-cv-toggle-icon] cambiando il
   * testo visibile. Esempio nel template:
   *   <button data-cv-toggle-theme>
   *     <span data-cv-toggle-icon></span>
   *   </button>
   */
  function updateToggleIcons(theme) {
    var icons = document.querySelectorAll('[data-cv-toggle-icon]');
    icons.forEach(function (el) {
      el.textContent = theme === THEME_LIGHT ? '◐' : '◑';
      el.setAttribute('aria-label',
        theme === THEME_LIGHT ? 'Switch to dark mode' : 'Switch to light mode'
      );
    });
  }

  /**
   * Legge il tema salvato. Se nulla e' salvato, default a dark.
   * NON usiamo prefers-color-scheme: l'identita' cyber/verso e' dark.
   */
  function getStoredTheme() {
    try {
      return localStorage.getItem(STORAGE_KEY) || THEME_DARK;
    } catch (e) {
      return THEME_DARK;
    }
  }

  /**
   * Salva la scelta dell'utente.
   */
  function setStoredTheme(theme) {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (e) {
      /* noop, localStorage potrebbe essere disabilitato */
    }
  }

  /**
   * Toggle pubblico, esposto su window per l'uso da onclick="..."
   */
  window.cvToggleTheme = function () {
    var current = getStoredTheme();
    var next = current === THEME_LIGHT ? THEME_DARK : THEME_LIGHT;
    setStoredTheme(next);
    applyTheme(next);
  };

  /**
   * Bind automatico su elementi con [data-cv-toggle-theme].
   */
  function bindToggleButtons() {
    var buttons = document.querySelectorAll('[data-cv-toggle-theme]');
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        window.cvToggleTheme();
      });
    });
  }

  /**
   * Mobile menu toggle. Bound to [data-cv-burger]; toggles the
   * `is-menu-open` class on the closest [data-cv-header] and keeps
   * aria-expanded in sync.
   *
   * Review fix 1.5 (17 Oct): the collapsed menu hid visually
   * (max-height: 0 + visibility: hidden in CSS) but its links kept
   * receiving Tab focus, so keyboard focus vanished off-screen.
   * `inert` on the collapsed nav removes it from the tab order and
   * the accessibility tree — but ONLY while the burger layout is
   * active: on desktop the very same nav is the always-visible top
   * bar, hence the media-query listener. Escape closes the open menu
   * and returns focus to the button. Browsers without inert (pre-2023)
   * ignore the property assignment and keep today's behaviour.
   */
  function bindBurgerButtons() {
    var mq = window.matchMedia('(max-width: 767px)');
    var burgers = document.querySelectorAll('[data-cv-burger]');
    burgers.forEach(function (btn) {
      var header = btn.closest('[data-cv-header]');
      if (!header) return;
      var navId = btn.getAttribute('aria-controls');
      var nav = navId ? document.getElementById(navId) : null;

      function syncInert() {
        if (!nav) return;
        nav.inert = mq.matches && !header.classList.contains('is-menu-open');
      }
      function setOpen(open) {
        header.classList.toggle('is-menu-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        syncInert();
      }

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        setOpen(!header.classList.contains('is-menu-open'));
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && header.classList.contains('is-menu-open')) {
          setOpen(false);
          btn.focus();
        }
      });
      if (mq.addEventListener) { mq.addEventListener('change', syncInert); }
      else if (mq.addListener) { mq.addListener(syncInert); }
      syncInert();
    });
  }

  /**
   * Reading progress bar. Tracks scroll position relative to .cv-article
   * boundaries and updates the [data-cv-reading-progress] bar's scaleX
   * (0..1). Uses requestAnimationFrame to coalesce scroll events and
   * keeps the work GPU-side via transform.
   *
   * Articles shorter than the viewport snap to scaleX(1) immediately.
   */
  function setupReadingProgress() {
    var bar = document.querySelector('[data-cv-reading-progress]');
    if (!bar) return;
    /* Long-form posts use .cv-article; daily briefs use .cv-bulletin-article.
       A page has at most one, so the first match is the right boundary. */
    var article = document.querySelector('.cv-article, .cv-bulletin-article');
    if (!article) return;

    var ticking = false;

    function update() {
      var rect = article.getBoundingClientRect();
      var totalScrollable = rect.height - window.innerHeight;
      var progress;
      if (totalScrollable <= 0) {
        progress = 1;
      } else {
        progress = (-rect.top) / totalScrollable;
        if (progress < 0) progress = 0;
        else if (progress > 1) progress = 1;
      }
      bar.style.transform = 'scaleX(' + progress + ')';
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, {passive: true});
    window.addEventListener('resize', onScroll, {passive: true});
    update();
  }

  /**
   * Search overlay. Hits Ghost's Content API for the latest 100 posts in
   * the current language collection (filter chosen from
   * <html data-cv-lang>) and substring-matches client-side on title and
   * excerpt. Cheap and reliable for blog-scale post counts; if the
   * archive ever exceeds ~1000 posts the right move is server-side
   * filter=title:~~q rather than fetch-all.
   *
   * Disabled (search button hidden) when no Content API key is exposed
   * via <meta name="cv-search-api-key">.
   */
  function setupSearch() {
    var triggers = document.querySelectorAll('[data-cv-toggle-search]');
    var overlay = document.querySelector('[data-cv-search]');
    if (!triggers.length || !overlay) return;

    var input = overlay.querySelector('[data-cv-search-input]');
    var resultsBox = overlay.querySelector('[data-cv-search-results]');
    var closers = overlay.querySelectorAll('[data-cv-search-close]');
    var apiKeyMeta = document.querySelector('meta[name="cv-search-api-key"]');
    var apiKey = apiKeyMeta ? apiKeyMeta.content : null;

    if (!apiKey) {
      triggers.forEach(function (el) { el.style.display = 'none'; });
      return;
    }

    var siteUrl = window.location.origin;
    var debounceTimer = null;
    var lastFocus = null;
    var cache = null;
    var labelEssays = resultsBox.getAttribute('data-cv-label-essays') || 'essays';
    var labelSignal = resultsBox.getAttribute('data-cv-label-signal') || 'signal';
    var labelDeep = resultsBox.getAttribute('data-cv-label-deep') || 'in the text';
    var msgDeepLoading = resultsBox.getAttribute('data-cv-msg-deep-loading') || 'searching the text…';
    var msgEmpty = resultsBox.getAttribute('data-cv-msg-empty') || 'no results';
    var msgError = resultsBox.getAttribute('data-cv-msg-error') || 'search unavailable';

    function open() {
      lastFocus = document.activeElement;
      overlay.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      setTimeout(function () { input.focus(); }, 50);
      /* Phase B kicks off at the FIRST opening, never at page load
         (review Part 5): megabytes of plaintext have no place in the
         critical path of a page view. */
      ensureBodies();
    }

    function close() {
      overlay.setAttribute('hidden', '');
      document.body.style.overflow = '';
      input.value = '';
      resultsBox.innerHTML = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    /**
     * Two result groups, essays always above the signal (the signal
     * architecture, Oct 2026 — the requirement this whole work was
     * born from: essays must never drown in the news). The divide is
     * the #signal umbrella tag, the same one that keeps the streams
     * off the homepage. Two queries so the sets never compete for the
     * same pagination:
     *   essays — language branch minus the signal (14/12 today)
     *   signal — tag:hash-signal, newest first, ALL pages (review fix
     *            1.6, 17 Oct, superseding the 16-Oct "latest 100"
     *            decision: the group hit 90 of the 100-per-page cap,
     *            growing five a week — older bulletins would have
     *            silently dropped out of the search by month's end).
     */
    function buildEssaysFilter() {
      var lang = document.documentElement.getAttribute('data-cv-lang');
      var base = 'visibility:public+tag:-hash-signal';
      if (lang === 'it') return base + '+tag:hash-it';
      return base + '+tag:hash-en';
    }

    function escape(s) {
      if (!s) return '';
      return String(s).replace(/[<>&"']/g, function (c) {
        return {'<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;',"'":'&#39;'}[c];
      });
    }

    function formatDate(iso) {
      if (!iso) return '';
      try {
        var d = new Date(iso);
        var month = d.toLocaleString('en-GB', {month: 'short'}).toLowerCase();
        var day = String(d.getDate()).padStart(2, '0');
        return day + ' ' + month + ' ' + d.getFullYear();
      } catch (e) {
        return '';
      }
    }

    function renderResult(post) {
      var date = formatDate(post.published_at);
      var topic = post.primary_tag ? post.primary_tag.slug : '';
      var excerpt = post.excerpt ? post.excerpt.slice(0, 160) + (post.excerpt.length > 160 ? '…' : '') : '';
      return '<a class="cv-search-result" href="' + escape(post.url) + '">' +
             '<p class="cv-search-result-meta">' +
                '<time>' + escape(date) + '</time>' +
                (topic ? ' <span class="cv-slash" aria-hidden="true">/</span> <span>' + escape(topic) + '</span>' : '') +
             '</p>' +
             '<h3 class="cv-search-result-title">' + escape(post.title) + '</h3>' +
             (excerpt ? '<p class="cv-search-result-excerpt">' + escape(excerpt) + '</p>' : '') +
             '</a>';
    }

    function queryUrl(filter, page) {
      return siteUrl + '/ghost/api/content/posts/' +
             '?key=' + encodeURIComponent(apiKey) +
             '&filter=' + encodeURIComponent(filter) +
             '&fields=id,title,url,excerpt,published_at' +
             '&include=tags' +
             '&limit=100' +
             '&page=' + (page || 1) +
             '&formats=' +
             '&order=' + encodeURIComponent('published_at desc');
    }

    /**
     * Every page of a filter, sequentially, up to
     * meta.pagination.pages with a hard stop at 10 pages (1 000
     * posts) as a cost guard. The flattened set is kept in
     * sessionStorage per filter, so reopening the search in the same
     * session does not redo the round trips; the key is versioned to
     * invalidate stale shapes on theme changes, and every storage
     * touch is wrapped — private windows may throw, and over quota
     * the in-memory `cache` still carries the session.
     */
    var SEARCH_CACHE_VER = 'cv-search-v2:';
    function fetchAll(filter) {
      var key = SEARCH_CACHE_VER + filter;
      try {
        var hit = sessionStorage.getItem(key);
        if (hit) return Promise.resolve(JSON.parse(hit));
      } catch (e) { /* storage unavailable: fall through to the fetch */ }
      function fetchPage(n, acc) {
        return fetch(queryUrl(filter, n)).then(function (r) { return r.json(); }).then(function (data) {
          acc = acc.concat((data && data.posts) || []);
          var pages = (data && data.meta && data.meta.pagination && data.meta.pagination.pages) || 1;
          if (n < pages && n < 10) return fetchPage(n + 1, acc);
          return acc;
        });
      }
      return fetchPage(1, []).then(function (all) {
        try { sessionStorage.setItem(key, JSON.stringify(all)); } catch (e) { /* quota/private: memory only */ }
        return all;
      });
    }

    function fetchPosts() {
      if (cache) return Promise.resolve(cache);
      return Promise.all([
        fetchAll(buildEssaysFilter()),
        fetchAll('visibility:public+tag:hash-signal')
      ]).then(function (both) {
        cache = {
          essays: both[0] || [],
          signal: both[1] || []
        };
        return cache;
      });
    }

    /**
     * Phase B of the search (review Part 5, 17 Oct): the BODY of the
     * signal issues. "NetID" appears five times in the China issue's
     * text and zero times in title or excerpt — without the body it
     * is simply not findable. plaintext is megabytes, so it is
     * fetched page by page over the #signal group only (the group
     * where body search earns its cost), kept in sessionStorage like
     * the metadata, and the open query re-renders when it lands.
     * Deep hits link THROUGH a text fragment (#:~:text=): the
     * browser scrolls to the matched passage, no anchor
     * infrastructure needed.
     */
    var bodies = null;
    var bodiesPromise = null;
    var bodiesFailed = false;

    function bodyQueryUrl(page) {
      return siteUrl + '/ghost/api/content/posts/' +
             '?key=' + encodeURIComponent(apiKey) +
             '&filter=' + encodeURIComponent('visibility:public+tag:hash-signal') +
             '&fields=id,title,url,published_at' +
             '&formats=plaintext' +
             '&limit=100' +
             '&page=' + page +
             '&order=' + encodeURIComponent('published_at desc');
    }

    function ensureBodies() {
      if (bodiesPromise) return bodiesPromise;
      var key = SEARCH_CACHE_VER + 'bodies:hash-signal';
      try {
        var hit = sessionStorage.getItem(key);
        if (hit) {
          bodies = JSON.parse(hit);
          bodiesPromise = Promise.resolve(bodies);
          return bodiesPromise;
        }
      } catch (e) { /* storage unavailable: fetch every time */ }
      function fetchPage(n, acc) {
        return fetch(bodyQueryUrl(n)).then(function (r) { return r.json(); }).then(function (data) {
          acc = acc.concat((data && data.posts) || []);
          var pages = (data && data.meta && data.meta.pagination && data.meta.pagination.pages) || 1;
          if (n < pages && n < 10) return fetchPage(n + 1, acc);
          return acc;
        });
      }
      bodiesPromise = fetchPage(1, []).then(function (all) {
        bodies = all;
        try { sessionStorage.setItem(key, JSON.stringify(all)); } catch (e) { /* quota/private: memory only */ }
        if (input.value && input.value.length >= 2) { search(input.value); }
        return all;
      }).catch(function () {
        bodiesFailed = true;
        bodies = [];
        if (input.value && input.value.length >= 2) { search(input.value); }
        return [];
      });
      return bodiesPromise;
    }

    function deepHref(url, q) {
      /* '-' is a context separator inside a text fragment: escape it
         on top of the usual percent-encoding. */
      return url + '#:~:text=' + encodeURIComponent(q).replace(/-/g, '%2D');
    }

    function deepSnippet(text, q) {
      var i = text.toLowerCase().indexOf(q.toLowerCase());
      if (i === -1) return '';
      var start = Math.max(0, i - 70);
      var end = Math.min(text.length, i + q.length + 90);
      return (start > 0 ? '…' : '') +
             text.slice(start, end).replace(/\s+/g, ' ') +
             (end < text.length ? '…' : '');
    }

    function renderDeepResult(post, q) {
      /* Locate the hit in the RAW snippet, then escape the three
         pieces separately: searching inside the escaped string could
         match within an entity ("am" in "&amp;") and split it. */
      var raw = deepSnippet(post.plaintext || '', q);
      var idx = raw.toLowerCase().indexOf(q.toLowerCase());
      var snippet = idx === -1 ? escape(raw) :
                    escape(raw.slice(0, idx)) +
                    '<mark class="cv-search-hit">' + escape(raw.slice(idx, idx + q.length)) + '</mark>' +
                    escape(raw.slice(idx + q.length));
      return '<a class="cv-search-result" href="' + escape(deepHref(post.url, q)) + '">' +
             '<p class="cv-search-result-meta"><time>' + escape(formatDate(post.published_at)) + '</time></p>' +
             '<h3 class="cv-search-result-title">' + escape(post.title) + '</h3>' +
             (snippet ? '<p class="cv-search-result-excerpt">' + snippet + '</p>' : '') +
             '</a>';
    }

    function search(q) {
      if (!q || q.length < 2) {
        resultsBox.innerHTML = '';
        return;
      }
      fetchPosts().then(function (groups) {
        var qLower = q.toLowerCase();
        function hits(list) {
          return list.filter(function (p) {
            return (p.title && p.title.toLowerCase().indexOf(qLower) !== -1) ||
                   (p.excerpt && p.excerpt.toLowerCase().indexOf(qLower) !== -1);
          });
        }
        var essays = hits(groups.essays);
        var signal = hits(groups.signal);
        /* Deep hits never duplicate an instant hit. */
        var seen = {};
        essays.concat(signal).forEach(function (p) { seen[p.id] = true; });
        var deep = null; /* null = still loading; [] = loaded, none */
        if (bodies) {
          deep = bodies.filter(function (p) {
            return !seen[p.id] && p.plaintext &&
                   p.plaintext.toLowerCase().indexOf(qLower) !== -1;
          }).slice(0, 12);
        } else if (!bodiesFailed) {
          ensureBodies();
        }
        var html = '';
        if (essays.length) {
          html += '<h3 class="cv-search-group">' + escape(labelEssays) + '</h3>' +
                  essays.map(renderResult).join('');
        }
        if (signal.length) {
          html += '<h3 class="cv-search-group">' + escape(labelSignal) + '</h3>' +
                  signal.map(renderResult).join('');
        }
        if (deep && deep.length) {
          html += '<h3 class="cv-search-group">' + escape(labelDeep) + '</h3>' +
                  deep.map(function (p) { return renderDeepResult(p, q); }).join('');
        }
        if (deep === null && !bodiesFailed) {
          html += '<p class="cv-search-loading">' + escape(msgDeepLoading) + '</p>';
        }
        if (!html) {
          resultsBox.innerHTML = '<p class="cv-search-empty">' + escape(msgEmpty) + '</p>';
          return;
        }
        resultsBox.innerHTML = html;
      }).catch(function () {
        resultsBox.innerHTML = '<p class="cv-search-empty">' + escape(msgError) + '</p>';
      });
    }

    triggers.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        open();
      });
    });

    closers.forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        close();
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !overlay.hasAttribute('hidden')) {
        close();
      }
    });

    input.addEventListener('input', function () {
      clearTimeout(debounceTimer);
      var q = input.value.trim();
      debounceTimer = setTimeout(function () { search(q); }, 200);
    });
  }

  /**
   * Brief / CTI content enhancer. Daily briefs (cv-bulletin-post) are
   * authored as markdown items shaped like:
   *
   *   **[P1]** [headline](url) — *source*
   *   Why it matters: ...
   *   ...summary...
   *   actor ... (NN%), escalation        // optional trailing meta line
   *
   * which markdown renders as one <p> per item, lines separated by <br>.
   * This pass adds the hooks markdown can't emit so §16 CSS can make a
   * brief scannable: a priority chip, a bold "why it matters" label, and
   * a dimmed mono meta line. No-op on plain (non-brief) digests, whose
   * paragraphs lack the link + <br> shape.
   */
  function enhanceBriefContent() {
    var root = document.querySelector('.cv-bulletin-content');
    if (!root) return;

    var whyRe = /^(\s*)(Why it matters:|Perché conta:)/i;
    var metaRe = /^\s*(actor\b|tlp:|<code>\s*cve-|`?cve-)/i;

    Array.prototype.forEach.call(root.querySelectorAll('p'), function (p) {
      if (!p.querySelector('a') || p.innerHTML.indexOf('<br') === -1) return;

      var lines = p.innerHTML.split(/<br\s*\/?>/i);
      var changed = false;
      lines = lines.map(function (line, i) {
        if (whyRe.test(line)) {
          changed = true;
          return line.replace(whyRe, '$1<span class="cv-brief-why">$2</span>');
        }
        if (i === lines.length - 1 && metaRe.test(line)) {
          changed = true;
          return '<span class="cv-brief-meta">' + line + '</span>';
        }
        return line;
      });
      if (changed) { p.innerHTML = lines.join('<br>'); }

      p.classList.add('cv-brief-item');

      var first = p.firstElementChild;
      if (first && first.tagName === 'STRONG' && /^\[P[0-9]\]$/.test(first.textContent.trim())) {
        first.classList.add('cv-brief-prio');
      }
      var headline = p.querySelector('a');
      if (headline) { headline.classList.add('cv-brief-headline'); }
    });
  }

  /**
   * Desk issues: a clickable table of contents built from the h2
   * section headings — Ghost already slugs stable ids onto content
   * headings, so the anchors exist server-side and this only adds
   * the index (review Part 4, 17 Oct). Needs three sections to be
   * worth the space. The label travels in data-cv-toc-label, set by
   * the template from the locales. Then every h2 of desk AND brief
   * bodies gets a visible pilcrow permalink: the section is the unit
   * people will share. Without JS nothing is missing — the ids are
   * already in the markup.
   */
  function buildSignalToc() {
    var content = document.querySelector('.cv-desk-content');
    if (content) {
      var heads = content.querySelectorAll('h2[id]');
      if (heads.length >= 3) {
        var label = content.getAttribute('data-cv-toc-label') || 'Contents';
        var nav = document.createElement('nav');
        nav.className = 'cv-toc';
        nav.setAttribute('aria-label', label);
        var head = document.createElement('p');
        head.className = 'cv-toc-label';
        head.textContent = label;
        var ol = document.createElement('ol');
        ol.className = 'cv-toc-list';
        Array.prototype.forEach.call(heads, function (h) {
          var li = document.createElement('li');
          var a = document.createElement('a');
          a.href = '#' + h.id;
          a.textContent = h.textContent;
          li.appendChild(a);
          ol.appendChild(li);
        });
        nav.appendChild(head);
        nav.appendChild(ol);
        content.insertBefore(nav, content.firstChild);
      }
    }
    var bodies = document.querySelectorAll('.cv-desk-content, .cv-bulletin-content');
    Array.prototype.forEach.call(bodies, function (body) {
      Array.prototype.forEach.call(body.querySelectorAll('h2[id]'), function (h) {
        var a = document.createElement('a');
        a.className = 'cv-section-anchor';
        a.href = '#' + h.id;
        a.setAttribute('aria-label', 'Permalink');
        a.textContent = '\u00b6';
        h.appendChild(a);
      });
    });
  }

  /**
   * The two-level bulletin (Paolo's call, 17 Oct): a brief opens with
   * its first h2 section — Top stories, the short part — and the rest
   * of the digest folds into a native <details> labelled "the full
   * digest · N sections". Nothing is reduced, only folded: without JS
   * everything stays open, and a navigation to an anchor INSIDE the
   * fold opens it first (load + hashchange). Chromium also auto-opens
   * details for text-fragment hits; elsewhere the deep-search link
   * degrades to the top of the issue and the summary is one click.
   * Runs AFTER enhanceBriefContent(), which must see the paragraphs
   * in place. Needs at least three sections to be worth it.
   */
  function foldBriefDigest() {
    var content = document.querySelector('.cv-bulletin-content');
    if (!content) return;
    var heads = content.querySelectorAll('h2[id]');
    if (heads.length < 3) return;

    var details = document.createElement('details');
    details.className = 'cv-brief-fold';
    var summary = document.createElement('summary');
    summary.className = 'cv-brief-fold-summary';
    var label = content.getAttribute('data-cv-fold-label') || 'the full digest';
    var word = content.getAttribute('data-cv-fold-word') || 'sections';
    summary.textContent = label + ' \u00b7 ' + (heads.length - 1) + ' ' + word;
    details.appendChild(summary);

    var inner = document.createElement('div');
    inner.className = 'cv-brief-fold-body';
    var second = heads[1];
    var node = second;
    var moved = [];
    while (node) { moved.push(node); node = node.nextSibling; }
    moved.forEach(function (n) { inner.appendChild(n); });
    details.appendChild(inner);
    content.appendChild(details);

    function openForHash() {
      var id = decodeURIComponent((window.location.hash || '').slice(1));
      if (!id) return;
      var target = document.getElementById(id);
      if (target && details.contains(target) && !details.open) {
        details.open = true;
        target.scrollIntoView();
      }
    }
    window.addEventListener('hashchange', openForHash);
    openForHash();
  }

  /**
   * On Italian pages, point every /tag/<slug>/ link at its Italian view
   * (?lang=it) so topic navigation stays in the reader's language.
   * tag.hbs renders both language groups; [data-cv-lang-only] + the
   * data-cv-lang attribute pick the one to show. English needs no param.
   * Since the 18-Oct round (part 3) the same pass also retargets the
   * service links — logo, breadcrumb "home", newsletter anchor — on
   * pages whose language is known only client-side (?lang=it): on /it/
   * pages the server already derives them from the cvHtmlLang block.
   */
  function localizeTagLinks() {
    if (document.documentElement.getAttribute('data-cv-lang') !== 'it') return;
    var links = document.querySelectorAll('a[href*="/tag/"], a[href$="/about/"], a[href$="/why-cyberverso/"], a[href$="/archive/"], a[href$="/privacy/"], a[href$="/signal/"], .cv-brand, .cv-archive-breadcrumb a, a[href*="#cv-newsletter"]');
    Array.prototype.forEach.call(links, function (a) {
      /* Never rewrite the language switcher: on the paired About pages
         its "en" link intentionally points at /about/ from an IT page. */
      if (a.closest('.cv-lang-switcher')) return;
      var href = a.getAttribute('href');
      if (!href || /[?&]lang=/.test(href)) return;
      var url;
      try { url = new URL(href, window.location.origin); } catch (e) { return; }
      if (url.origin !== window.location.origin) return;
      /* Service links to the site root — the logo, the breadcrumb
         "home", the newsletter anchor — follow the page's language
         (18-Oct round, part 3). On /it/ pages the server already
         emits /it/ targets (cvHtmlLang), so this only ever fires on
         pages whose language is known client-side (?lang=it). */
      if (url.pathname === '/') {
        a.setAttribute('href', '/it/' + url.hash);
        return;
      }
      /* The About page has a real Italian counterpart at /it/about/
         (routes.yaml, data: page.about-it): point the footer link there. */
      if (url.pathname === '/about/') {
        a.setAttribute('href', '/it/about/' + url.hash);
        return;
      }
      if (url.pathname === '/why-cyberverso/') {
        a.setAttribute('href', '/it/perche-cyberverso/' + url.hash);
        return;
      }
      if (url.pathname === '/archive/') {
        a.setAttribute('href', '/it/archive/' + url.hash);
        return;
      }
      if (url.pathname === '/privacy/') {
        a.setAttribute('href', '/it/privacy/' + url.hash);
        return;
      }
      if (url.pathname === '/signal/') {
        a.setAttribute('href', '/it/signal/' + url.hash);
        return;
      }
      if (!/^\/tag\/[^/]+\/$/.test(url.pathname)) return;
      url.searchParams.set('lang', 'it');
      a.setAttribute('href', url.pathname + url.search + url.hash);
    });
  }

  /**
   * /archive/ pages: insert a year heading whenever the year of a row's
   * <time datetime> changes (the list is newest-first, so years descend
   * monotonically). The server renders the list flat with full dates:
   * without JS nothing is missing — this only adds the grouping, and
   * .is-grouped hides the now-redundant per-row year via CSS.
   */
  function groupArchiveByYear() {
    var list = document.querySelector('[data-cv-archive-list]');
    if (!list) return;
    var current = null;
    Array.prototype.forEach.call(list.querySelectorAll('.cv-archive-row'), function (row) {
      var time = row.querySelector('time[datetime]');
      if (!time) return;
      var year = (time.getAttribute('datetime') || '').slice(0, 4);
      if (!year || year === current) return;
      current = year;
      var li = document.createElement('li');
      li.className = 'cv-archive-year';
      li.innerHTML = '<span class="cv-slash" aria-hidden="true">/</span> ' + year;
      list.insertBefore(li, row);
    });
    if (current) { list.classList.add('is-grouped'); }
  }

  /**
   * Portal is deliberately excluded from ghost_head, so nothing handles
   * data-members-form: without this guard a native submit would GET the
   * email address into the URL (and from there into the analytics
   * page_hit payload). Block the submit and show a translated notice
   * until the Portal-free signup flow ships.
   */
  function guardNewsletterForms() {
    var forms = document.querySelectorAll('form[data-members-form]');
    Array.prototype.forEach.call(forms, function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var note = form.parentElement.querySelector('[data-members-error]');
        if (!note) return;
        var it = document.documentElement.getAttribute('data-cv-lang') === 'it';
        var msg = (it && form.getAttribute('data-cv-guard-msg-it')) ||
                  form.getAttribute('data-cv-guard-msg') || '';
        note.textContent = msg;
        if (it) { note.setAttribute('lang', 'it'); }
      });
    });
  }

  /**
   * Derived issue numbering for the desk boards (the Part-6 decision,
   * Oct 2026). Ghost(Pro) has no custom fields and Handlebars no
   * arithmetic, so the board serves data-cv-desk-total (= the number
   * of the LATEST issue) on page 1 and this fills each row with
   * "n. NN" counting down. Progressive enhancement: without JS the
   * rows stay date-led. The numbering is derived from the collection
   * count — the editorial rule "a published issue is never deleted
   * nor moved" is what keeps published numbers stable.
   */
  function numberDeskRows() {
    var lists = document.querySelectorAll('[data-cv-desk-total]');
    Array.prototype.forEach.call(lists, function (list) {
      var total = parseInt(list.getAttribute('data-cv-desk-total'), 10);
      if (!total) return;
      var slots = list.querySelectorAll('[data-cv-desk-num]');
      for (var i = 0; i < slots.length; i++) {
        var n = total - i;
        if (n < 1) break;
        slots[i].textContent = 'n. ' + (n < 10 ? '0' + n : n);
      }
    });
  }

  /**
   * Brief headlines in the lists (22 Oct 2026, pipeline change): a
   * brief is now titled "Cyber / Brief, 22 Oct 2026: <headline>". Where
   * the list already shows the date and the brand as their own
   * elements — the board's date column, the home line's mark and date,
   * the hub row — keep only the headline. Only elements marked
   * data-cv-brief-title are touched, and only when the title has the
   * new shape: the old "Cyber / Brief — 22 Oct 2026" titles do not
   * match and stay whole. The full title moves to the title attribute
   * (visible on hover, and the link's accessible name keeps the date
   * from its <time>). Without JS the full title renders.
   */
  function trimBriefTitles() {
    var re = /^\s*Cyber\s*\/\s*Brief,\s*\d{1,2}\s+\S+\s+\d{4}:\s+(.+)$/;
    var nodes = document.querySelectorAll('[data-cv-brief-title]');
    Array.prototype.forEach.call(nodes, function (el) {
      var full = el.textContent;
      var m = re.exec(full);
      if (!m) return;
      el.setAttribute('title', full.trim());
      el.textContent = m[1].trim();
    });
  }

  function initBindings() {
    bindToggleButtons();
    bindBurgerButtons();
    setupReadingProgress();
    enhanceBriefContent();
    foldBriefDigest();
    setupSearch();
    buildSignalToc();
    localizeTagLinks();
    groupArchiveByYear();
    guardNewsletterForms();
    numberDeskRows();
    trimBriefTitles();
  }

  /**
   * Marca la lingua attiva su <html data-cv-lang="..."> in base al
   * pathname. Il language switcher fuori dalle pagine post (renderizzato
   * staticamente in cv-language-switcher.hbs) si appoggia a questo
   * attributo via CSS per evidenziare il link corretto.
   * Dal giro del 18 ott (parte 3) lo stesso rilevamento gira anche
   * inline in head (default.hbs), prima del primo paint, e imposta pure
   * documentElement.lang: qui il re-set è idempotente — stessa logica,
   * la query vince sul percorso — e aggiunge solo aria-current.
   */
  function detectLanguage() {
    var path = window.location.pathname || '/';
    var isIt = path === '/it' || path === '/it/' || path.indexOf('/it/') === 0;
    /* Tag pages live at /tag/<slug>/ for both languages (Ghost has a
       single tag taxonomy route), so they carry the language as
       ?lang=it|en instead; the query wins over the path. */
    var q = /[?&]lang=(en|it)(?:&|$)/.exec(window.location.search || '');
    var lang = q ? q[1] : (isIt ? 'it' : 'en');
    document.documentElement.setAttribute('data-cv-lang', lang);

    /* Mark the active static-switcher link with aria-current so screen
       readers announce the current language correctly. The CSS-only
       pointer-events:none alone doesn't communicate state to AT. */
    var activeLinks = document.querySelectorAll(
      '.cv-lang-switcher .cv-lang[data-cv-lang="' + lang + '"]'
    );
    activeLinks.forEach(function (el) {
      el.setAttribute('aria-current', 'page');
    });
  }

  /* Inizializzazione: applica subito il tema salvato e marca la lingua,
     poi attacca i listener quando il DOM e' pronto. */
  detectLanguage();
  applyTheme(getStoredTheme());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBindings);
  } else {
    initBindings();
  }

})();


/* =============================================================
   FLASH-OF-WRONG-THEME PREVENTION
   -------------------------------------------------------------
   Per evitare che la pagina lampeggi in light mode mentre il
   JS si carica, si aggiunge anche un piccolo blocco INLINE
   in default.hbs nell'<head>:

   <script>
     (function () {
       try {
         var t = localStorage.getItem('cv-theme') || 'dark';
         if (t === 'dark') {
           document.documentElement.classList.add('has-light-text');
         } else {
           document.documentElement.classList.add('cv-light-mode');
         }
       } catch (e) {
         document.documentElement.classList.add('has-light-text');
       }
     })();
   </script>

   Questo blocco viene fornito separatamente (file inline-theme-init.html).
   ============================================================= */
